import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadFile } from '../api/files';
import { patchModel } from '../api/models';
import { AdminModelView } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';

interface ModelImageUploaderProps {
    model: AdminModelView;
    onImageUpdate?: (newImageFilename: string) => void;
}

const ModelImageUploader: React.FC<ModelImageUploaderProps> = ({
                                                                   model,
                                                                   onImageUpdate
                                                               }) => {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [uploadingImage, setUploadingImage] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Generate current image URL using imgproxy when available
    const getCurrentImageUrl = () => pickImageUrl(model.imageUrls, model.imageFilename);

    const uploadMutation = useMutation({
        mutationFn: ({ file, desiredName }: { file: File; desiredName?: string }) =>
            uploadFile(file, desiredName),
        onSuccess: (data) => {
            const key = Object.values(data)[0] as string;
            updateImageMutation.mutate(key);
        },
        onError: (error) => {
            console.error('Upload failed:', error);
            setError('Failed to upload image. Please try again.');
            setUploadingImage(false);
            setPreviewUrl(null);
        }
    });

    const updateImageMutation = useMutation({
        mutationFn: (imageFilename: string) =>
            patchModel(model.id, { imageFilename }),
        onSuccess: (updatedModel) => {
            queryClient.invalidateQueries({ queryKey: ['model', model.id] });
            queryClient.invalidateQueries({ queryKey: ['models'] });

            setUploadingImage(false);
            setPreviewUrl(null);
            setError(null);

            onImageUpdate?.(updatedModel.imageFilename || '');
        },
        onError: (error) => {
            console.error('Image update failed:', error);
            setError('Failed to update model image. Please try again.');
            setUploadingImage(false);
            setPreviewUrl(null);
        }
    });

    const validateFile = (file: File): string | null => {
        // Check file type
        if (!file.type.startsWith('image/')) {
            return 'Please select an image file (JPEG, PNG, GIF, WebP)';
        }

        // Check file size (max 10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            return 'Image size should be less than 10MB';
        }

        // Check image dimensions (optional)
        return null;
    };

    const processImageUpload = (file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        setUploadingImage(true);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Generate desired filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const desiredName = `${model.modelUsername}-avatar-${timestamp}`;

        // Upload file
        uploadMutation.mutate({ file, desiredName });
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            processImageUpload(file);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            processImageUpload(file);
        }
    };

    const handleRemoveImage = () => {
        if (confirm('Are you sure you want to remove the current image?')) {
            updateImageMutation.mutate('');
        }
    };

    const currentImageUrl = getCurrentImageUrl();
    const displayImageUrl = previewUrl || currentImageUrl;

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Profile Image</h3>
                <div className="text-sm text-gray-500">
                    Current: {model.imageFilename || 'No image'}
                </div>
            </div>

            {error && (
                <div className="notification-error mb-4">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                {/* Current/Preview Image */}
                {displayImageUrl ? (
                    <div className="relative">
                        <div className="w-48 h-48 mx-auto rounded-lg overflow-hidden border-2 border-gray-200">
                            <img
                                src={displayImageUrl}
                                alt={`${model.modelName || model.modelUsername} profile`}
                                className="w-full h-full object-cover"
                                onError={() => {
                                    console.warn('Failed to load image:', displayImageUrl);
                                }}
                            />

                            {uploadingImage && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                    <div className="text-center text-white">
                                        <div className="spinner mx-auto mb-2" />
                                        <p className="text-sm">Uploading...</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Image Actions */}
                        <div className="flex justify-center gap-2 mt-4">
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="btn btn-primary btn-sm"
                                disabled={uploadingImage}
                            >
                                📷 Change Image
                            </button>

                            {model.imageFilename && !uploadingImage && (
                                <button
                                    onClick={handleRemoveImage}
                                    className="btn btn-danger btn-sm"
                                    disabled={updateImageMutation.isPending}
                                >
                                    🗑️ Remove
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Upload Area */
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                            dragActive
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => !uploadingImage && fileInputRef.current?.click()}
                        style={{ cursor: uploadingImage ? 'not-allowed' : 'pointer' }}
                    >
                        {uploadingImage ? (
                            <div className="space-y-3">
                                <div className="spinner mx-auto" />
                                <p className="text-gray-600">Uploading image...</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="text-6xl">
                                    {dragActive ? '📤' : '📷'}
                                </div>
                                <div>
                                    <p className="text-lg font-medium text-gray-900 mb-1">
                                        {dragActive ? 'Drop image here' : 'Upload profile image'}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Click to browse or drag & drop
                                    </p>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Supports: JPEG, PNG, GIF, WebP • Max size: 10MB
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Hidden File Input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                    disabled={uploadingImage}
                />

                {/* Upload Progress Info */}
                {uploadingImage && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-blue-700">
                            <div className="spinner" />
                            <div className="text-sm">
                                <p className="font-medium">Processing image upload...</p>
                                <p className="text-xs text-blue-600">
                                    This may take a few moments depending on file size
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload Guidelines */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">📋 Upload Guidelines</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Recommended resolution: 400x400px or higher</li>
                        <li>• Square images work best for profile pictures</li>
                        <li>• File formats: JPEG, PNG, GIF, WebP</li>
                        <li>• Maximum file size: 10MB</li>
                        <li>• Images are automatically optimized after upload</li>
                    </ul>
                </div>

                {/* Technical Info */}
                {model.imageFilename && (
                    <details className="text-sm">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                            Technical Details
                        </summary>
                        <div className="mt-2 p-3 bg-gray-100 rounded font-mono text-xs">
                            <p><strong>Filename:</strong> {model.imageFilename}</p>
                            <p><strong>Model ID:</strong> {model.id}</p>
                            <p><strong>Username:</strong> {model.modelUsername}</p>
                            {displayImageUrl && (
                                <p><strong>URL:</strong> <a href={displayImageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View Image</a></p>
                            )}
                        </div>
                    </details>
                )}
            </div>
        </div>
    );
};

export default ModelImageUploader;